/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { motion } from "framer-motion";
import { Heart, Code, Sparkles, Users, ArrowUpRight } from "lucide-react";

const values = [
  { icon: Sparkles, label: "Créer des projets utiles" },
  { icon: Code, label: "Passionné de web, design & SEO" },
  { icon: Users, label: "Accompagner les petites entreprises" },
];

export const QuiSuisJe = () => (
  <section
    id="qui-suis-je"
    data-testid="quisuisje-section"
    aria-labelledby="qui-suis-je-title"
    className="py-24 md:py-32 bg-paper relative overflow-hidden"
  >
    <div
      className="absolute top-0 right-0 w-[600px] h-[600px] -z-0 pointer-events-none"
      style={{ background: "radial-gradient(closest-side, rgba(6,182,212,0.10), transparent 70%)" }}
      aria-hidden="true"
    />
    <div className="absolute inset-0 dot-grid opacity-20 -z-0 pointer-events-none" aria-hidden="true" />

    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Portrait */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden border border-ink/10 bg-paper-soft relative">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_webup-agency/artifacts/3o1hpg5d_image.png"
                alt="Marius Nougaillon, designer web indépendant et fondateur de l'agence WebUP à Marssac-sur-Tarn"
                title="Marius Nougaillon — Fondateur de WebUP"
                width="800"
                height="1000"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
              <div
                className="absolute inset-0 bg-gradient-to-tr from-ink/25 via-transparent to-transparent"
                aria-hidden="true"
              />
            </div>
            {/* Signature card */}
            <div className="hidden md:flex absolute -bottom-6 -right-6 bg-white border border-ink/10 shadow-[0_20px_50px_-25px_rgba(11,30,61,0.35)] p-5 flex-col gap-1 max-w-[260px]">
              <p className="font-mono-label text-cyan-600">Fondateur WebUP</p>
              <p className="font-display text-2xl text-ink">Marius Nougaillon</p>
              <p className="text-xs text-ink/60">Designer web — Marssac-sur-Tarn (81)</p>
            </div>
          </div>
        </motion.div>

        {/* Text */}
        <div className="lg:col-span-7">
          <p className="font-mono-label text-cyan-600 flex items-center">
            <span className="rule bg-cyan" /> À propos
          </p>
          <h2
            id="qui-suis-je-title"
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]"
          >
            Qui se cache <em className="italic font-light">derrière WebUP</em> ?
          </h2>

          <div className="mt-8 space-y-5 text-ink/80 leading-relaxed text-[17px]">
            <p>
              Je m'appelle <strong className="text-ink">Marius Nougaillon</strong>, créateur de sites
              internet indépendant, basé à Marssac-sur-Tarn.
            </p>
            <p>
              Mon métier : concevoir des sites{" "}
              <strong className="text-ink">clairs, rapides et rassurants</strong> pour que les petites
              entreprises gagnent en visibilité et attirent plus de clients.
            </p>
            <ul className="space-y-2 pl-5 list-disc marker:text-cyan-500">
              <li>Passionné par le web, le design et la visibilité sur Google ;</li>
              <li>Un seul interlocuteur, de l'idée à la mise en ligne ;</li>
              <li>Trop de petites entreprises n'ont pas le site qu'elles méritent — et si nous changions ça ensemble&nbsp;?</li>
            </ul>
          </div>

          {/* Association callout */}
          <motion.aside
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 relative overflow-hidden bg-gradient-to-br from-ink via-ink to-ink-700 text-white p-8"
            aria-label="Engagement associatif WebUP"
          >
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-cyan/30 rounded-full blur-3xl" aria-hidden="true" />
            <div className="relative">
              <p className="font-mono-label text-cyan-300 flex items-center gap-2">
                <Heart size={14} className="text-cyan-300" aria-hidden="true" />
                Un engagement qui compte
              </p>
              <p className="mt-4 font-display text-xl md:text-2xl leading-snug">
                Chaque site WebUP aide à financer{" "}
                <span className="text-cyan-300 italic">Des Rêves Libres</span> — une
                association qui accompagne les jeunes dans leurs projets.
              </p>
              <p className="mt-3 text-white/75 text-sm leading-relaxed max-w-2xl">
                Faire de belles choses, et en faire du bien.
              </p>
            </div>
          </motion.aside>

          {/* Values chips */}
          <ul className="mt-10 flex flex-wrap gap-3" aria-label="Mes valeurs professionnelles">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <li
                  key={v.label}
                  className="inline-flex items-center gap-2 px-4 h-11 bg-white border border-ink/10 text-sm text-ink/85"
                >
                  <Icon size={14} className="text-cyan-600" aria-hidden="true" />
                  {v.label}
                </li>
              );
            })}
          </ul>

          <a
            href="#contact"
            data-testid="quisuisje-cta"
            className="mt-10 inline-flex items-center gap-3 h-14 px-7 btn-premium text-sm font-medium group"
            aria-label="Prendre contact avec Marius Nougaillon, fondateur de WebUP"
          >
            Échangeons sur votre projet
            <ArrowUpRight
              size={18}
              aria-hidden="true"
              className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </a>
        </div>
      </div>
    </div>
  </section>
);
